import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { INSTRUMENTS } from "@/data/instruments";
import { Trash2, ChevronLeft } from "lucide-react";
export default function History() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    const params = filter === "all" ? {} : { kind: filter };
    const all = readAll(); setItems(filter === "all" ? all : all.filter(x => x.kind === filter));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try {
      await api.delete(`/assessments/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const meta = (kind) => INSTRUMENTS[kind] || { label: kind, tag: "" };

  return (
    <PageContainer testid="history-page" wide>
      <Link
        to="/portal/continuum/tools"
        className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4"
        data-testid="history-back"
      >
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader
        overline="Portal · History"
        title="Your assessment history."
        subtitle="Every assessment you save appears here. Filter by instrument to review trends."
      />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="history-filter">
        {[{ id: "all", label: "All" }, ...Object.entries(INSTRUMENTS).map(([id, meta]) => ({ id, label: meta.label }))].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`btn ${filter === f.id ? "btn-primary" : "btn-secondary"}`}
            data-testid={`history-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 card overflow-hidden" data-testid="history-list">
          {items.length === 0 ? (
            <div className="p-8 text-center text-ink-muted text-sm">
              No saved assessments yet.{" "}
              <Link to="/portal/continuum/tools" className="text-teal underline underline-offset-2">
                Try a tool.
              </Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-ink-muted">
                  <th className="p-3 font-medium">Instrument</th>
                  <th className="p-3 font-medium">Patient</th>
                  <th className="p-3 font-medium">Score</th>
                  <th className="p-3 font-medium hidden md:table-cell">Interpretation</th>
                  <th className="p-3 font-medium hidden md:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => setSelected(a)}
                    className={`cursor-pointer hover:bg-cream transition-colors border-t border-line-soft ${
                      selected?.id === a.id ? "bg-teal-light/30" : ""
                    }`}
                    data-testid={`history-row-${a.id}`}
                  >
                    <td className="p-3">
                      <div className="text-navy font-medium">{meta(a.kind).label}</div>
                      <div className="text-xs text-ink-muted">{meta(a.kind).tag}</div>
                    </td>
                    <td className="p-3 text-ink">{a.patient_identifier || "—"}</td>
                    <td className="p-3 text-navy font-medium">{a.score ?? "—"}</td>
                    <td className="p-3 text-ink-muted hidden md:table-cell">{a.interpretation || "—"}</td>
                    <td className="p-3 text-ink-muted hidden md:table-cell text-xs">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="card p-6 lg:sticky lg:top-6 self-start" data-testid="history-detail">
              <div className="overline mb-1">{meta(selected.kind).label}</div>
              <h3 className="text-navy text-xl font-semibold">
                Score: {selected.score ?? "—"}
              </h3>
              {selected.interpretation && (
                <div className="badge badge-teal mt-2">{selected.interpretation}</div>
              )}
              <div className="rule my-4" />
              <dl className="text-sm space-y-2">
                {selected.patient_identifier && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-ink-muted">Patient</dt>
                    <dd className="text-ink">{selected.patient_identifier}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-muted">Saved</dt>
                  <dd className="text-ink">{new Date(selected.created_at).toLocaleString()}</dd>
                </div>
                {selected.notes && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-ink-muted">Notes</dt>
                    <dd className="text-ink whitespace-pre-wrap">{selected.notes}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-muted">Responses</dt>
                  <dd>
                    <pre className="text-xs bg-cream border border-line-soft rounded-md p-3 mt-1 overflow-auto max-h-80">
                      {JSON.stringify(selected.responses, null, 2)}
                    </pre>
                  </dd>
                </div>
              </dl>

              <div className="rule my-4" />
              <button
                onClick={() => remove(selected.id)}
                className="text-sm text-rust hover:underline inline-flex items-center gap-1"
                data-testid="history-delete"
              >
                <Trash2 size={13} strokeWidth={2} /> Delete assessment
              </button>
            </div>
          ) : (
            <div className="card p-6 text-sm text-ink-muted text-center">
              Select an assessment to view details.
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
